import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { RotateLeft } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import React, { useEffect, useState } from 'react';
import { IconButton, LinearProgress } from '@material-ui/core';

import ResultsTable from './ResultsTable';
import PxwSelectionForm from './PxwSelectionForm';
import { BackButton, TextField } from '../../components/AtomComponents';

import { mapSimulateResponse } from './utils';
import useDataService from '../../hooks/useDataService';
import apiToUrlMap, { formatString } from '../../ApiMapping';

import { eMessageType } from '../../types/IMessageType';
import { ISimulateResponse, IGeneratedPayload, IGeneratedResult } from './types';

import './PxwProcessDevices.scss';

const PxwProcessDevices = () => {
  const userRoles = useSelector((state: any) => state.userState.role);

  const [isLoading, setIsLoading] = useState(false);
  const [tenantList, setTenantList] = useState<string[] | null>();
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [generatedResult, setGeneratedResult] = useState<IGeneratedResult | null>(null);

  const { fetchUrl, openSnackBar } = useDataService();

  useEffect(() => {
    if (!userRoles['SANDOX_TESTER']) return;

    const loadTenants = async () => {
      try {
        const result: { tenantIds: string[] } = await fetchUrl(
          'GET',
          apiToUrlMap.pxwDeviceTestSandboxTenants
        );
        setTenantList(result.tenantIds ?? []);
      } catch (e: any) {
        openSnackBar(e.message || 'Unable to fetch tenants', eMessageType.error);
      }
    };
    loadTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles]);

  const handleTenantChange = (_: any, value: string | null) => {
    setSelectedTenant(value);
    setGeneratedResult(null);
  };

  const handleReset = () => {
    setSelectedTenant(null);
    setGeneratedResult(null);
  };

  const handleSynthesize = async (payload: IGeneratedPayload) => {
    try {
      setIsLoading(true);
      const { selectedItem, ...body } = payload;
      const res: ISimulateResponse = await fetchUrl(
        'POST',
        formatString(apiToUrlMap.pxwDeviceTestSandboxSimulate, { tenantId: selectedTenant }),
        {
          body,
        }
      );
      setGeneratedResult({
        groupedDevices: mapSimulateResponse(res),
        item: selectedItem,
        clearPayload: {
          tenantId: selectedTenant!,
          poNumber: payload.purchaseOrderNumber,
          itemNumber: payload.itemNumber,
        },
      });
    } catch (e: any) {
      openSnackBar(e.message || 'Something went wrong', eMessageType.error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!userRoles['SANDOX_TESTER']) return <Redirect to="/dashboard" />;

  return (
    <div className="grid-y pxw-process-devices">
      {/* Header */}
      <div className="pxw-process-devices__header">
        <BackButton />
        <h3 className="margin-0">PXW Process Devices</h3>
        <div className="pxw-process-devices__tenant-select">
          <Autocomplete
            options={tenantList || []}
            value={selectedTenant}
            getOptionLabel={(o) => o}
            onChange={handleTenantChange}
            renderInput={(params) => (
              <TextField {...params} label="Select Tenant" variant="outlined" />
            )}
          />
        </div>
        {selectedTenant && (
          <IconButton size="small" title="Reset all selections" onClick={handleReset}>
            <RotateLeft />
          </IconButton>
        )}
      </div>

      {!tenantList ? (
        <div className="cell margin-top-1 margin-bottom-2 small-12 medium-7">
          <LinearProgress />
        </div>
      ) : (
        <>
          <PxwSelectionForm
            selectedTenant={selectedTenant}
            isLoading={isLoading}
            onSynthesize={handleSynthesize}
            onResultInvalidated={() => setGeneratedResult(null)}
          />

          {isLoading ? (
            <div className="cell margin-top-1 margin-bottom-2 small-12">
              <LinearProgress />
            </div>
          ) : generatedResult ? (
            <ResultsTable
              groupedDevices={generatedResult.groupedDevices}
              selectedItem={generatedResult.item}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default PxwProcessDevices;
